import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import HelpButton from 'help/HelpButton';
import {BLUE_DARKER, GRAY_SEMIDARK} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';

export function BlastColorLegend(): JSX.Element {
  return (
    <div className={css(styles.container)}>
      <div className={css(styles.title)}>Alignment Score</div>
      <div className={css(styles.helpButton)}>
        <HelpButton
          interaction={FloatingInteraction.HOVER}
          placement={FloatingPlacement.BOTTOM}
          renderContent={() => (
            <div className={css(styles.helpPopover)}>
              <div className={css(styles.helpText)}>
                Alignment scores reflect the similarity between a query sequence and a subject sequence.
                Higher scores indicate greater similarity, while lower scores suggest more differences between
                the sequences.
              </div>
              <div>
                <a
                  className={css(styles.helpLink)}
                  href="https://www.nlm.nih.gov/ncbi/workshops/2023-08_BLAST_evol/blast_score.html"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more about BLAST alignment score
                </a>
              </div>
            </div>
          )}
        />
      </div>
      <div className={css(styles.colorCircle1)} />
      <div className={css(styles.label)}>&lt;40</div>
      <div className={css(styles.colorCircle2)} />
      <div className={css(styles.label)}>40 - 50</div>
      <div className={css(styles.colorCircle3)} />
      <div className={css(styles.label)}>50 - 80</div>
      <div className={css(styles.colorCircle4)} />
      <div className={css(styles.label)}>80 - 200</div>
      <div className={css(styles.colorCircle5)} />
      <div className={css(styles.label)}>&gt;=200</div>
    </div>
  );
}

const colorSquare = {
  width: '16px',
  height: '16px',
  border: `1px solid ${GRAY_SEMIDARK}`,
  marginLeft: '18px',
};

const styles = StyleSheet.create({
  container: {
    display: 'inline-flex',
    alignItems: 'flex-start',
    borderRadius: '5px',
    border: `1px solid ${Color.GRAY_200}`,
    padding: '8px 16px',
  },
  title: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '16px',
  },
  helpButton: {
    marginLeft: '8px',
  },
  helpPopover: {
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  helpText: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: '14px',
  },
  helpLink: {
    fontSize: '13px',
    fontWeight: 600,
    lineHeight: '16px',
  },
  legend: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
  },
  colorCircle1: {
    ...colorSquare,
    background: `${Color.TEAL_200}`,
  },
  colorCircle2: {
    ...colorSquare,
    background: `${Color.TEAL_300}`,
  },
  colorCircle3: {
    ...colorSquare,
    background: `${Color.TEAL_400}`,
  },
  colorCircle4: {
    ...colorSquare,
    background: `${Color.TEAL_500}`,
  },
  colorCircle5: {
    ...colorSquare,
    background: `${BLUE_DARKER}`,
  },
  label: {
    marginLeft: '6px',
  },
});
